import PropTypes from 'prop-types';

import { CourseBackground, resolveCourseBackgroundKey } from '../course-backgrounds';
import PersonAvatar from './primitives/PersonAvatar';
import './subjectCard.css';

/**
 * A course/group as a card, headed by the subject artwork — the same catalogue
 * band the admin courses page uses and the same art the timetable watermarks
 * its cells with, so one subject looks like itself everywhere.
 *
 * Three stacked parts, in the order you read them: the band names the record,
 * an optional person row says whose it is, and a grid of stat tiles reports the
 * figures. Every one of them is also a control — the title opens the record,
 * the person opens their profile, and each tile is the way to the thing it
 * counts. That is what lets the card carry no overflow menu: a "⋯" that hides
 * three destinations behind a glyph is worse than three tiles that name them.
 *
 * `bgKey` is the background an admin chose; when they never chose one the
 * subject's own name picks the artwork, so a card is never a blank slab.
 */
const SubjectCard = ({ bgKey, themeName, title, titleLabel, subtitle, person, stats, size, onOpen }) => {
  const themeKey = resolveCourseBackgroundKey(bgKey, themeName ?? title);
  const personInteractive = person && typeof person.onOpen === 'function';

  return (
    <article
      className={`lms-subject-card${size === 'lg' ? ' lms-subject-card--lg' : ''}`}
    >
      <CourseBackground themeKey={themeKey} className="lms-subject-card__band">
        <div className="lms-subject-card__band-inner">
          <button
            type="button"
            className="lms-subject-card__title"
            onClick={onOpen}
            title={titleLabel}
          >
            {title}
          </button>
          {subtitle != null && subtitle !== '' && (
            <span className="lms-subject-card__subtitle">{subtitle}</span>
          )}
        </div>
      </CourseBackground>

      {/* The person sits on the card's own surface, not on the artwork: a face
          needs a plain ground to read against, and the band's scrim is tuned
          for type. */}
      {person && person.name && (
        <div className="lms-subject-card__person">
          <PersonAvatar
            person={{ fullName: person.name }}
            photoUrl={person.photoUrl}
            size={size === 'lg' ? 'md' : 'sm'}
            onOpen={person.onOpen}
            label={personInteractive ? person.label : undefined}
          />
          {personInteractive ? (
            <button
              type="button"
              className="lms-subject-card__person-name"
              onClick={person.onOpen}
              title={person.label}
            >
              {person.name}
            </button>
          ) : (
            <span className="lms-subject-card__person-name lms-subject-card__person-name--static">
              {person.name}
            </span>
          )}
        </div>
      )}

      <div className="lms-subject-card__stats">
        {stats.map(({ key, icon, label, value, tone, wide, onClick, title: statTitle }) => (
          <button
            key={key}
            type="button"
            className={[
              'lms-subject-card__stat',
              tone ? `lms-subject-card__stat--${tone}` : '',
              wide ? 'lms-subject-card__stat--wide' : '',
              value == null ? 'lms-subject-card__stat--bare' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={onClick}
            title={statTitle}
          >
            <span className="lms-subject-card__stat-label">
              {icon && (
                <span className="lms-subject-card__stat-icon" aria-hidden="true">
                  {icon}
                </span>
              )}
              {label}
            </span>
            {/* A tile with nothing to report is still a destination — it just
                reads as the action it is, with no figure under it. */}
            {value != null && (
              <span className="lms-subject-card__stat-value lms-figure">{value}</span>
            )}
          </button>
        ))}
      </div>
    </article>
  );
};

SubjectCard.propTypes = {
  /** The background an admin chose; null falls through to `themeName`. */
  bgKey: PropTypes.string,
  /** The subject name the artwork is inferred from when `bgKey` is unset.
      Defaults to `title`, which is wrong for a group — pass the course name. */
  themeName: PropTypes.string,
  title: PropTypes.node.isRequired,
  /** Says where the title goes, for the tooltip and the accessible name. */
  titleLabel: PropTypes.string,
  subtitle: PropTypes.node,
  /** Whose record this is. Given `onOpen`, the photo and the name open a profile. */
  person: PropTypes.shape({
    name: PropTypes.string,
    photoUrl: PropTypes.string,
    onOpen: PropTypes.func,
    label: PropTypes.string,
  }),
  /** One tile per figure worth carrying — each one is also its own control. */
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      icon: PropTypes.node,
      label: PropTypes.node.isRequired,
      /** Omit where the tile is purely a destination and counts nothing. */
      value: PropTypes.node,
      /** A functional hue makes the tile a state; omit for a plain reading. */
      tone: PropTypes.oneOf(['garnet', 'saffron', 'pistachio', 'majolica']),
      /** Spans the full row — for the one tile that does not share a line. */
      wide: PropTypes.bool,
      onClick: PropTypes.func,
      title: PropTypes.string,
    }),
  ),
  /** `lg` for a card that carries no person row and would otherwise read short. */
  size: PropTypes.oneOf(['md', 'lg']),
  onOpen: PropTypes.func,
};

SubjectCard.defaultProps = {
  bgKey: null,
  themeName: undefined,
  titleLabel: undefined,
  subtitle: null,
  person: null,
  stats: [],
  size: 'md',
  onOpen: undefined,
};

export default SubjectCard;
